import React, { useEffect, useState } from 'react';
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  DollarSign, Users, Plane, Calendar, TrendingUp,
  BookOpen, CheckCircle, XCircle, BarChart3, ArrowRight
} from 'lucide-react';
import StatCard from '../components/StatCard';
import { getAnalytics, PROFIT_RATE } from '../firebase/firestore';
import { Link } from 'react-router-dom';

const COLORS = ['#2D5BE3', '#9B59B6', '#00C853', '#FF1744', '#D4AF37'];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAnalytics().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-[#2D5BE3] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return <div className="text-[#546E7A]">Failed to load analytics.</div>;

  const bookingTypes = [
    { name: 'Flights', value: data.flightBookings },
    { name: 'Events', value: data.eventBookings },
  ];

  return (
    <div className="space-y-6">
      {/* Brand header */}
      <div className="bg-gradient-to-r from-[#1A3C8F]/40 to-[#0D1F4F]/60 border border-[#2D5BE3]/30 rounded-2xl p-5">
        <div className="flex items-center gap-4">
          <img src="/metamax-logo.png" alt="MetaMax" className="w-14 h-14 object-contain rounded-xl" />
          <div>
            <h2 className="text-white font-bold text-xl tracking-widest">META MAX</h2>
            <p className="text-[#D4AF37] text-sm font-medium">TRAVEL BEYOND LIMITS</p>
            <p className="text-[#546E7A] text-xs mt-0.5">Admin Dashboard — Live data from Firestore</p>
          </div>
        </div>
      </div>

      {/* Reporting shortcut */}
      <Link
        to="/reporting"
        className="flex items-center justify-between bg-[#1A2235] border border-[#1E2D45] rounded-2xl px-5 py-4
          hover:border-[#2D5BE3]/50 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#2D5BE3]/20 flex items-center justify-center">
            <BarChart3 size={20} className="text-[#2D5BE3]" />
          </div>
          <div>
            <div className="text-white font-semibold">Full Reports & CSV Export</div>
            <div className="text-[#546E7A] text-sm">Bookings by month, revenue split, user signups</div>
          </div>
        </div>
        <ArrowRight size={18} className="text-[#546E7A] group-hover:text-[#2D5BE3] transition-colors" />
      </Link>

      {/* Profit banner */}
      <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#A0832A]/10 border border-[#D4AF37]/30
        rounded-2xl p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
          <TrendingUp size={20} className="text-[#D4AF37]" />
        </div>
        <div>
          <div className="text-white font-bold text-lg">
            Platform Profit: ${data.platformProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div className="text-[#B0BEC5] text-sm">
            {(PROFIT_RATE * 100)}% of ${data.totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })} total revenue
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Revenue" value={`$${data.totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          icon={DollarSign} color="#D4AF37" trend={12} />
        <StatCard title="Total Users" value={data.totalUsers.toLocaleString()}
          icon={Users} color="#2D5BE3" trend={8} />
        <StatCard title="Total Bookings" value={data.totalBookings.toLocaleString()}
          icon={BookOpen} color="#9B59B6" trend={5} />
        <StatCard title="Catalog Items" value={(data.totalFlights + data.totalEvents).toLocaleString()}
          sub={`${data.totalFlights} flights · ${data.totalEvents} events`}
          icon={Plane} color="#00C853" />
      </div>

      {/* Recent bookings */}
      {data.recentBookings?.length > 0 && (
        <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-4">Recent Bookings</h3>
          <div className="space-y-2">
            {data.recentBookings.map(b => (
              <div key={b.id || b.bookingRef}
                className="flex items-center justify-between py-2 border-b border-[#1E2D45]/50 last:border-0">
                <div>
                  <span className="text-white text-sm font-mono">{b.bookingRef || b.id?.slice(0, 8)}</span>
                  <span className="text-[#546E7A] text-xs ml-2 capitalize">{b.type}</span>
                </div>
                <div className="text-right">
                  <div className="text-[#D4AF37] text-sm font-semibold">${(b.totalPrice || 0).toLocaleString()}</div>
                  <div className="text-[#546E7A] text-xs">{b.bookedAtLabel}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Revenue chart */}
      <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-white font-semibold">Revenue & Profit</h3>
            <p className="text-[#546E7A] text-sm">Last 6 months</p>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-[#2D5BE3]">
              <span className="w-3 h-3 rounded-full bg-[#2D5BE3]" /> Revenue
            </span>
            <span className="flex items-center gap-1.5 text-[#D4AF37]">
              <span className="w-3 h-3 rounded-full bg-[#D4AF37]" /> Profit (10%)
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data.monthlyRevenue}>
            <defs>
              <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2D5BE3" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#2D5BE3" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="profGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E2D45" />
            <XAxis dataKey="month" stroke="#546E7A" tick={{ fontSize: 12 }} />
            <YAxis stroke="#546E7A" tick={{ fontSize: 12 }}
              tickFormatter={v => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
            <Tooltip
              contentStyle={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 12 }}
              labelStyle={{ color: '#fff' }}
              formatter={(v, n) => [`$${v.toLocaleString()}`, n === 'revenue' ? 'Revenue' : 'Profit']}
            />
            <Area type="monotone" dataKey="revenue" stroke="#2D5BE3" fill="url(#revGrad)" strokeWidth={2} />
            <Area type="monotone" dataKey="profit" stroke="#D4AF37" fill="url(#profGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Booking types */}
        <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-4">Booking Types</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={bookingTypes} cx="50%" cy="50%" innerRadius={50} outerRadius={75}
                dataKey="value" paddingAngle={4}>
                {bookingTypes.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 12 }} />
              <Legend formatter={v => <span className="text-[#B0BEC5] text-xs">{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Status */}
        <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-4">Booking Status</h3>
          <div className="space-y-3 mt-6">
            <div className="flex items-center gap-3">
              <CheckCircle size={18} className="text-green-400" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#B0BEC5]">Confirmed</span>
                  <span className="text-white font-semibold">{data.confirmed}</span>
                </div>
                <div className="h-2 bg-[#1E2D45] rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full"
                    style={{ width: `${data.totalBookings ? (data.confirmed / data.totalBookings * 100) : 0}%` }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <XCircle size={18} className="text-red-400" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#B0BEC5]">Cancelled</span>
                  <span className="text-white font-semibold">{data.cancelled}</span>
                </div>
                <div className="h-2 bg-[#1E2D45] rounded-full overflow-hidden">
                  <div className="h-full bg-red-500 rounded-full"
                    style={{ width: `${data.totalBookings ? (data.cancelled / data.totalBookings * 100) : 0}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-4">Platform Overview</h3>
          <div className="space-y-3">
            {[
              { label: 'Total Events', value: data.totalEvents, icon: Calendar, color: '#9B59B6' },
              { label: 'Total Flights', value: data.totalFlights, icon: Plane, color: '#2D5BE3' },
              { label: 'Registered Users', value: data.totalUsers, icon: Users, color: '#00C853' },
              { label: 'Destinations', value: data.totalDestinations ?? 0, icon: BookOpen, color: '#D4AF37' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="flex items-center gap-3 p-3 bg-[#111827] rounded-xl">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: `${color}20` }}>
                  <Icon size={16} style={{ color }} />
                </div>
                <span className="text-[#B0BEC5] text-sm flex-1">{label}</span>
                <span className="text-white font-bold">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
